import { useHeadsUpClick } from "@/components/heads-up/context/heads-up-context";
import { SmartNoteRow } from "@/components/heads-up/cards/common/smart-note-row";
import { SmartCardSection, NoteList } from "@/components/heads-up/cards/common/layout";
import { CollapsibleSectionHeader } from "@/components/heads-up/cards/common/collapsible-section-header";
import { useIncrementalReveal } from "@/components/heads-up/cards/common/use-incremental-reveal";
import { useCallback, useMemo } from "react";
import type { FC, MouseEvent } from "react";
import type { Maybe } from "@/domains/common/types";
import type { MentionedByNotesListSmartCard } from "@/components/heads-up/heads-up.types";
import { SeeMoreButton } from "@/components/heads-up/cards/common/see-more-button";
import type { SmartNotePreviewInfo } from "@/components/heads-up/cards/common/smart-note-preview.types";
import type {
  HeadsUpCardRendererAnalytics,
  HeadsUpCardItemMeta,
} from "@/components/heads-up/cards/registry";
import { HeadsUpItemSource } from "@/components/heads-up/analytics";

type Props = {
  card: MentionedByNotesListSmartCard;
  getNotePreviewInfo?: (noteId: string) => Maybe<SmartNotePreviewInfo>;
  analytics?: HeadsUpCardRendererAnalytics;
};

export const MentionedByNotesListSmartCardRenderer: FC<Props> = ({
  card,
  getNotePreviewInfo,
  analytics,
}) => {
  const clickHandlers = useHeadsUpClick();

  const sectionTitle = card.value.title;
  const baseItemSource = card.originated_from_find_more
    ? HeadsUpItemSource.FindMore
    : HeadsUpItemSource.Standard;

  const items = useMemo(
    () =>
      card.value.items.map((item, index) => ({
        id: item.note_id,
        primary: item.primary_label ?? "Untitled",
        positionIndex: index,
      })),
    [card]
  );

  const { visibleItems, hasMore, showAll, reset } = useIncrementalReveal({
    cardId: card.id,
    items,
    initialCount: 3,
    step: 5,
  });

  const buildAnalyticsMeta = useCallback(
    (item: (typeof items)[number]): HeadsUpCardItemMeta => ({
      itemKind: "note",
      itemId: item.id,
      itemTitle: item.primary,
      itemPositionIndex: item.positionIndex,
      itemSource: baseItemSource,
    }),
    [baseItemSource]
  );

  const handleNoteClick = useCallback(
    (item: (typeof items)[number]) => (event: MouseEvent<HTMLElement>) => {
      analytics?.onItemClick?.(buildAnalyticsMeta(item));
      const handler = clickHandlers?.noteClick(item.id);
      handler?.(event);
    },
    [analytics, buildAnalyticsMeta, clickHandlers]
  );

  const content = (
    <NoteList>
      {visibleItems.map(item => (
        <SmartNoteRow
          key={item.id}
          noteId={item.id}
          primary={item.primary}
          onClick={handleNoteClick(item)}
          onPreviewOpen={() => analytics?.onItemPreviewOpen?.(buildAnalyticsMeta(item))}
          notePreviewInfo={getNotePreviewInfo?.(item.id)}
        />
      ))}
      <SeeMoreButton hasMore={hasMore} onClick={showAll} />
    </NoteList>
  );

  return (
    <SmartCardSection>
      <CollapsibleSectionHeader title={sectionTitle} onCollapseChange={c => c && reset()}>
        {content}
      </CollapsibleSectionHeader>
    </SmartCardSection>
  );
};
